import { IEvent } from "../interfaces/models/IEvent";

export interface CalendarEventPayload {
  title: string;
  description?: string;
  location: string;
  startDate: number;
  endDate: number;
  timeZone: string | null;
}

export interface CalendarLinkOption {
  id: string;
  label: string;
  href?: string;
  downloadIcs?: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

export function getWallClockParts(timestamp: number) {
  const date = new Date(timestamp * 1000);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hours: date.getUTCHours(),
    minutes: date.getUTCMinutes(),
  };
}

function formatCompactDateTime(timestamp: number): string {
  const p = getWallClockParts(timestamp);
  return `${p.year}${pad2(p.month)}${pad2(p.day)}T${pad2(p.hours)}${pad2(p.minutes)}00`;
}

function formatOutlookDateTime(timestamp: number): string {
  const p = getWallClockParts(timestamp);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hours)}:${pad2(p.minutes)}:00`;
}

function formatIcsDateTime(timestamp: number): string {
  return formatCompactDateTime(timestamp);
}

function resolveIanaTimezone(timeZone?: string): string | null {
  if (!timeZone || !timeZone.includes("/")) {
    return null;
  }

  try {
    Intl.DateTimeFormat("en-US", { timeZone: timeZone });
    return timeZone;
  } catch {
    return null;
  }
}

function resolveEndTimestamp(startDate: number, endDate?: number): number {
  if (endDate && endDate > startDate) {
    return endDate;
  }
  return startDate + 3600;
}

function getLocationString(event: IEvent): string {
  if (event.setupType === "webinar" || event.setupType === "groupWebinar") {
    return "Virtual";
  }
  if (event.comingSoonLocation) {
    return event.comingSoonLocationMessage || "Location to be determined";
  }
  if (event.address?.cityName && event.address?.countryName) {
    const parts = [
      event.address.address1,
      event.address.address2,
      event.address.cityName,
      event.address.stateName,
      event.address.zipCode,
      event.address.countryName,
    ].filter(Boolean);
    return parts.join(", ");
  }
  return event.location || "";
}

export function buildCalendarEventPayload(
  event: IEvent,
): CalendarEventPayload | null {
  if (!event.startDate) {
    return null;
  }

  return {
    title: event.title,
    description: event.description?.substring(0, 1000),
    location: getLocationString(event),
    startDate: event.startDate,
    endDate: resolveEndTimestamp(event.startDate, event.endDate),
    timeZone: resolveIanaTimezone(event.timeZone),
  };
}

export function buildGoogleCalendarUrl(payload: CalendarEventPayload): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: payload.title,
    dates: `${formatCompactDateTime(payload.startDate)}/${formatCompactDateTime(payload.endDate)}`,
  });

  if (payload.description) {
    params.set("details", payload.description);
  }
  if (payload.location) {
    params.set("location", payload.location);
  }
  if (payload.timeZone) {
    params.set("ctz", payload.timeZone);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildOutlookComposeUrl(
  baseUrl: string,
  payload: CalendarEventPayload,
): string {
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent", // Outlook deeplink action, not AddEvent.com
    startdt: formatOutlookDateTime(payload.startDate),
    enddt: formatOutlookDateTime(payload.endDate),
    subject: payload.title,
  });

  if (payload.description) {
    params.set("body", payload.description);
  }
  if (payload.location) {
    params.set("location", payload.location);
  }

  return `${baseUrl}?${params.toString()}`;
}

export function buildOutlookComUrl(payload: CalendarEventPayload): string {
  return buildOutlookComposeUrl(
    "https://outlook.live.com/calendar/0/deeplink/compose",
    payload,
  );
}

export function buildOffice365Url(payload: CalendarEventPayload): string {
  return buildOutlookComposeUrl(
    "https://outlook.office.com/calendar/0/deeplink/compose",
    payload,
  );
}

export function buildYahooCalendarUrl(payload: CalendarEventPayload): string {
  const params = new URLSearchParams({
    v: "60",
    title: payload.title,
    st: formatCompactDateTime(payload.startDate),
    et: formatCompactDateTime(payload.endDate),
  });

  if (payload.description) {
    params.set("desc", payload.description);
  }
  if (payload.location) {
    params.set("in_loc", payload.location);
  }

  return `https://calendar.yahoo.com/?${params.toString()}`;
}

function escapeIcsText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

export function buildIcsContent(payload: CalendarEventPayload): string {
  const dtStart = payload.timeZone
    ? `DTSTART;TZID=${payload.timeZone}:${formatIcsDateTime(payload.startDate)}`
    : `DTSTART:${formatIcsDateTime(payload.startDate)}`;
  const dtEnd = payload.timeZone
    ? `DTEND;TZID=${payload.timeZone}:${formatIcsDateTime(payload.endDate)}`
    : `DTEND:${formatIcsDateTime(payload.endDate)}`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//WSG//Event Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${payload.startDate}-${payload.endDate}@wsg`,
    dtStart,
    dtEnd,
    `SUMMARY:${escapeIcsText(payload.title)}`,
  ];

  if (payload.description) {
    lines.push(`DESCRIPTION:${escapeIcsText(payload.description)}`);
  }
  if (payload.location) {
    lines.push(`LOCATION:${escapeIcsText(payload.location)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

export function downloadIcsFile(payload: CalendarEventPayload): void {
  const content = buildIcsContent(payload);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${payload.title.replace(/[^a-z0-9]/gi, "_").substring(0, 50) || "event"}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getCalendarLinkOptions(
  payload: CalendarEventPayload,
): CalendarLinkOption[] {
  return [
    {
      id: "google",
      label: "Google Calendar",
      href: buildGoogleCalendarUrl(payload),
    },
    {
      id: "outlook",
      label: "Outlook.com",
      href: buildOutlookComUrl(payload),
    },
    {
      id: "office365",
      label: "Office 365",
      href: buildOffice365Url(payload),
    },
    {
      id: "yahoo",
      label: "Yahoo Calendar",
      href: buildYahooCalendarUrl(payload),
    },
    {
      id: "apple",
      label: "Apple Calendar",
      downloadIcs: true,
    },
  ];
}
